/**
 * scanCodes — the pure half of "scan a printed QR and land on the right page" (2026-10-07).
 *
 * A printed QR holds {site}/s/{kind}-{key} (jobq-api scan_codes.py). The code names WHAT
 * the paper is; GET /scan/resolve says which record that is and targetForScan() picks
 * the page here, so a page can move without reprinting paper that is already on the floor.
 *
 * Kinds so far:
 *   task-<task_id>        the next station's task the traveler prints as "Task # N"
 *   job-<master_job_num>  a traveler with no next task (Ready to Ship, a closed remake)
 *   mtn-<mtn_id>          a material lot (TRK = the mtn_id, zero-padded): a pallet beacon's
 *                         screen (2026-10-08). Opens that TRK's popup on Material Tracking.
 *   bcn-<n>               a pallet beacon (BCN-003 -> bcn-3): an AVAILABLE beacon's screen.
 *                         Opens the Beacons page on it, to put it on a pallet.
 *   plt-<pallet_id>       a sales-order pallet (planned_pallets): a beacon's screen while it rides
 *                         an SO pallet (2026-10-22). Opens the pallet check (/pallet-check/<id>,
 *                         2026-10-23), where anyone checks the pallet against JobQ.
 *   sb-<bundle_id>        a surplus or owed bundle on the surplus rack: its 4x6 label prints a
 *                         Data Matrix (the laser Zebra has no QR; 2026-10-26). Opens the Surplus
 *                         Rack page on that bundle (/surplus-rack?bundle=12).
 *
 * parseScanCode accepts the bare code, the whole link (a phone opens it; a USB or
 * Bluetooth scanner TYPES it into whatever field has focus), and any letter case.
 */

// Same rule as jobq-api scan_codes._CODE_RE — keep the two in step.
const CODE_RE = /(?:^|\/s\/)\s*(task|job|mtn|bcn|plt|sb)[-\s]?(\d{1,9})\s*\/?\s*$/i;

/** {kind, key, code} for a scanned or typed code, or null when it is not one of ours. */
export function parseScanCode(text) {
  if (text == null) return null;
  const m = CODE_RE.exec(String(text).trim());
  if (!m) return null;
  const kind = m[1].toLowerCase();
  const key = parseInt(m[2], 10);
  return { kind, key, code: `${kind}-${key}` };
}

/** The in-app path that resolves a code: /s/task-37114. */
export function scanPath(parsed) {
  return parsed ? `/s/${parsed.code}` : null;
}

/** How a code reads while it is looked up: "task #37114", "job #9403", "TRK 000409", "BCN-003",
 *  "pallet #493" (the code carries the pallet_id; its PLT-XXXX comes with the answer). */
export function scanLabel(parsed) {
  if (!parsed) return "scan";
  if (parsed.kind === "mtn") return `TRK ${String(parsed.key).padStart(6, "0")}`;
  if (parsed.kind === "bcn") return `BCN-${String(parsed.key).padStart(3, "0")}`;
  if (parsed.kind === "plt") return `pallet #${parsed.key}`;
  if (parsed.kind === "sb") return `surplus bundle #${parsed.key}`;
  return `${parsed.kind} #${parsed.key}`;
}

/** The Material Tracking tab with this lot's TRK popup open (MaterialEntryPage reads it). */
export function materialTrackingPath(mtnId) {
  return `/materials-entry?tab=tracking&mtn=${mtnId}`;
}

/**
 * What a /materials-entry link asks for: {tab: 'tracking', mtnId} (open that TRK's popup),
 * {tab: 'tracking', mtnId: null} (just the tab), or null (an ordinary visit).
 */
export function materialsEntryDeepLink(search) {
  const params = new URLSearchParams(search || "");
  const raw = params.get("mtn");
  const mtnId = raw && /^\d{1,9}$/.test(raw.trim()) ? parseInt(raw, 10) : null;
  if (mtnId && mtnId > 0) return { tab: "tracking", mtnId };
  if (params.get("tab") === "tracking") return { tab: "tracking", mtnId: null };
  return null;
}

/** The Beacons page focused on one beacon (BeaconsPage reads ?beacon=). */
export function beaconPath(beaconId) {
  return `/beacons?beacon=${encodeURIComponent(beaconId)}`;
}

/** The Surplus Rack page on one bundle (SurplusRackPage reads ?bundle=). */
export function surplusRackPath(bundleId) {
  return `/surplus-rack?bundle=${encodeURIComponent(bundleId)}`;
}

/** The bundle a /surplus-rack link asks to show (?bundle=12), or null. */
export function surplusRackLink(search) {
  const raw = new URLSearchParams(search || "").get("bundle");
  if (!raw || !/^\d{1,9}$/.test(raw.trim())) return null;
  const id = parseInt(raw, 10);
  return id > 0 ? id : null;
}

/** The surplus rack on one crate (a scanned crate beacon: its bundles first, highlighted). */
export function surplusRackCratePath(crateId) {
  return `/surplus-rack?crate=${encodeURIComponent(crateId)}`;
}

/** The crate a /surplus-rack link asks to show (?crate=7), or null. */
export function surplusRackCrateLink(search) {
  const raw = new URLSearchParams(search || "").get("crate");
  if (!raw || !/^\d{1,9}$/.test(raw.trim())) return null;
  const id = parseInt(raw, 10);
  return id > 0 ? id : null;
}

/** The pallet check screen for a sales-order pallet (PalletCheckPage). */
export function palletCheckPath(palletId) {
  return `/pallet-check/${encodeURIComponent(palletId)}`;
}

/** The Sales Orders page on one order with one pallet in view (SalesOrderPage reads &pallet=). */
export function salesOrderPalletPath(salesOrderId, palletId) {
  return `/sales-orders?id=${encodeURIComponent(salesOrderId)}&pallet=${encodeURIComponent(palletId)}`;
}

/** The pallet a /sales-orders link asks to show (&pallet=493), or null. */
export function salesOrderPalletLink(search) {
  const raw = new URLSearchParams(search || "").get("pallet");
  if (!raw || !/^\d{1,9}$/.test(raw.trim())) return null;
  const id = parseInt(raw, 10);
  return id > 0 ? id : null;
}

// The stations the Job Queue page can show (its dropdown and the mobile picker).
export const QUEUE_STATIONS = ["Laser", "Tap", "Brake", "Fabrication", "Blast", "Coating", "Finish"];

const CLOSED_TASK = new Set(["Completed", "Voided", "Cancelled"]);
const CLOSED_JOB = new Set(["Voided", "Cancelled"]);

/**
 * Where a resolved code goes. Returns either
 *   { navigate: "/queue?station=Brake&task=37114&job=812" }   go straight there, or
 *   { message, links: [{label, to}] }                         say why not, offer the way on.
 * `resolved` is GET /scan/resolve's answer.
 */
export function targetForScan(resolved) {
  if (!resolved || !resolved.kind) return { message: "JobQ didn't recognise that code.", links: [] };
  const jobLink = resolved.job_id
    ? { label: `Open job #${resolved.master_job_num ?? resolved.job_id}`, to: `/jobs/${resolved.job_id}` }
    : null;

  if (resolved.kind === "job") {
    return { navigate: `/jobs/${resolved.job_id}` };
  }

  if (resolved.kind === "mtn" && resolved.mtn_id) {
    return { navigate: materialTrackingPath(resolved.mtn_id) };
  }

  // a surplus crate's beacon (2026-11-03): the rack on that crate
  if (resolved.kind === "bcn" && resolved.crate_id) {
    return { navigate: surplusRackCratePath(resolved.crate_id) };
  }

  if (resolved.kind === "bcn" && resolved.beacon_id) {
    return { navigate: beaconPath(resolved.beacon_id) };
  }

  // A sales-order pallet's beacon: the pallet check screen (2026-10-23; it opened the order with the
  // pallet in view before). The pallet_id is all it needs; the code itself carries it.
  if (resolved.kind === "plt" && resolved.pallet_id) {
    return { navigate: palletCheckPath(resolved.pallet_id) };
  }

  if (resolved.kind === "sb" && resolved.bundle_id) {
    return { navigate: surplusRackPath(resolved.bundle_id) };
  }

  if (resolved.kind === "task") {
    const station = resolved.workstation;
    const where = resolved.sub_process && station === "Fabrication" ? resolved.sub_process : station;
    if (CLOSED_JOB.has(resolved.job_status)) {
      return {
        message: `Job #${resolved.master_job_num} was ${String(resolved.job_status).toLowerCase()}, so task #${resolved.task_id} (${where}) has nothing left to do.`,
        links: [jobLink].filter(Boolean),
      };
    }
    if (CLOSED_TASK.has(resolved.status)) {
      return {
        message: `Task #${resolved.task_id} at ${where} is already ${String(resolved.status).toLowerCase()}.`,
        links: [
          jobLink,
          QUEUE_STATIONS.includes(station) ? { label: `Open the ${where} queue`, to: `/queue?station=${encodeURIComponent(station)}` } : null,
        ].filter(Boolean),
      };
    }
    if (!QUEUE_STATIONS.includes(station)) {
      // Shipping and anything else without a station queue: the job shows every task.
      return { navigate: `/jobs/${resolved.job_id}` };
    }
    const qs = new URLSearchParams({ station, task: String(resolved.task_id), job: String(resolved.job_id) });
    return { navigate: `/queue?${qs.toString()}` };
  }

  return { message: "JobQ didn't recognise that code.", links: [] };
}

/**
 * The task a /queue link asks for (?station=&task=&job=), or null. All three must be
 * there and sane; anything else is an ordinary /queue visit.
 */
export function queueDeepLink(search) {
  const params = new URLSearchParams(search || "");
  const station = params.get("station");
  const taskId = parseInt(params.get("task"), 10);
  const jobId = parseInt(params.get("job"), 10);
  if (!station || !QUEUE_STATIONS.includes(station)) return null;
  if (!Number.isInteger(taskId) || taskId <= 0 || !Number.isInteger(jobId) || jobId <= 0) return null;
  return { station, taskId, jobId };
}
